import { useState, type FormEvent } from 'react';
import { gatewaySettingsApi, type GatewaySettingsView } from '../../api/client';
import { Button, Card, Chip, Field, LoadError, Loading, Notice } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System → Call gateway (1.13.0, Teampace 2845): the call gateway's address and token,
 * kept in Stitch instead of EasyPanel's GATEWAY_URL and GATEWAY_TOKEN. A change to a gateway in use
 * takes effect within seconds, with no redeploy. The token is sealed when saved and never shown again.
 */
function From({ source, name, changed }: { source: GatewaySettingsView['url']['source']; name: string; changed: GatewaySettingsView['url']['changed'] }) {
  const text =
    source === 'stitch'
      ? `Saved in Stitch${changed ? ` · ${shortWhen(changed.at)}${changed.byName ? ` by ${changed.byName}` : ''}` : ''}`
      : source === 'easypanel'
        ? `From EasyPanel (${name})`
        : 'Not set';
  return (
    <small className="muted" style={{ display: 'block' }}>
      {text}
    </small>
  );
}

export function GatewayCard() {
  const data = useData(() => gatewaySettingsApi.get(), []);
  const [view, setView] = useState<GatewaySettingsView | null>(null);
  const v = view ?? data.data;
  const [url, setUrl] = useState<string | null>(null);
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  if (data.error) {
    return (
      <Card title="Call gateway">
        <LoadError error={data.error} retry={data.reload} />
      </Card>
    );
  }
  if (!v) {
    return (
      <Card title="Call gateway">
        <Loading />
      </Card>
    );
  }

  async function run(key: string, fn: () => Promise<void>) {
    setBusy(key);
    setProblem(null);
    setNote(null);
    try {
      await fn();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy('');
    }
  }

  const typedUrl = url ?? v.url.value ?? '';
  const changed = (url !== null && url.trim() !== (v.url.value ?? '')) || Boolean(token.trim());
  const stillInEasyPanel = v.easypanel.filter((x) => !x.inStitch);
  const canDelete = v.easypanel.filter((x) => x.inStitch);

  async function save(e: FormEvent) {
    e.preventDefault();
    await run('save', async () => {
      const change: { url?: string; token?: string } = {};
      if (url !== null && url.trim() !== (v!.url.value ?? '')) change.url = url.trim();
      if (token.trim()) change.token = token.trim();
      setView(await gatewaySettingsApi.save(change));
      setUrl(null);
      setToken('');
      setNote({ ok: true, text: 'Saved. Stitch uses it within a few seconds.' });
    });
  }

  return (
    <Card title="Call gateway">
      <form className="card-body" onSubmit={(e) => void save(e)}>
        <p className="muted" style={{ marginTop: 0 }}>
          Calls in the Stitch apps go through the call gateway. Its address and the token its installer printed can be kept here instead of in EasyPanel. A change takes effect within a few seconds, with
          no redeploy.
        </p>
        <div style={{ marginBottom: 12 }}>
          {v.ready ? <Chip tone="ok">Set up</Chip> : <Chip tone="warn">Not set up</Chip>}{' '}
          {v.active ? <Chip tone="ok">App calling on</Chip> : <Chip>App calling off</Chip>}
        </div>
        {v.restartNeeded && (
          <Notice tone="warn" title="Restart Stitch once to start app calling">
            App calling was off when Stitch started. Restart the Stitch app in EasyPanel once, and it uses this gateway from then on. Later changes need no restart.
          </Notice>
        )}
        {!v.canSeal && (
          <Notice tone="warn" title="The token can’t be saved here yet">
            Set DATA_ENCRYPTION_KEY in EasyPanel and redeploy once. It locks the token before it’s saved.
          </Notice>
        )}
        {v.token.unreadable && <Notice tone="crit">The saved token can’t be opened with this DATA_ENCRYPTION_KEY. Save it again.</Notice>}
        {stillInEasyPanel.length > 0 && (
          <Notice tone="neutral" title="Still set in EasyPanel">
            <p style={{ margin: '0 0 8px' }}>{stillInEasyPanel.map((x) => x.name).join(', ')}. Move them here once, then delete them from EasyPanel.</p>
            <Button
              icon="download"
              disabled={busy !== ''}
              onClick={() =>
                void run('move', async () => {
                  const r = await gatewaySettingsApi.move();
                  setView(r.settings);
                  setNote({
                    ok: true,
                    text: [r.moved.length ? `Moved into Stitch: ${r.moved.join(', ')}.` : 'Nothing to move.', r.kept.length ? `${r.kept.join(' and ')} stays in EasyPanel until DATA_ENCRYPTION_KEY is set.` : '']
                      .filter(Boolean)
                      .join(' ')
                  });
                })
              }
            >
              {busy === 'move' ? 'Moving…' : 'Move from EasyPanel'}
            </Button>
          </Notice>
        )}
        {canDelete.length > 0 && (
          <Notice tone="ok" title="You can delete these from EasyPanel">
            {canDelete.map((x) => x.name).join(', ')}: Stitch uses its own now. Delete them in EasyPanel → Environment whenever you next redeploy.
          </Notice>
        )}
        <Field
          label="Address"
          placeholder="https://gateway.example.com or http://10.0.0.5:8091"
          value={typedUrl}
          onChange={(e) => setUrl(e.target.value)}
          spellCheck={false}
          hint="The gateway’s control address, as its installer printed it: https, or http on a private network."
        />
        <From source={v.url.source} name="GATEWAY_URL" changed={v.url.changed} />
        <Field
          label="Token"
          type="password"
          autoComplete="new-password"
          spellCheck={false}
          disabled={!v.canSeal}
          placeholder={v.token.set ? 'Set (leave empty to keep it)' : 'The token the installer printed'}
          value={token}
          onChange={(e) => setToken(e.target.value)}
        />
        <From source={v.token.source} name="GATEWAY_TOKEN" changed={v.token.changed} />
        {problem && <Notice tone="crit">{problem}</Notice>}
        {note && <Notice tone={note.ok ? 'ok' : 'warn'}>{note.text}</Notice>}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <Button type="submit" variant="primary" disabled={!changed || busy !== ''}>
            {busy === 'save' ? 'Saving…' : 'Save'}
          </Button>
          <Button
            disabled={busy !== '' || (!v.ready && !(typedUrl.trim() && token.trim()))}
            onClick={() =>
              void run('test', async () => {
                const typed: { url?: string; token?: string } = {};
                if (url !== null && url.trim()) typed.url = url.trim();
                if (token.trim()) typed.token = token.trim();
                const r = await gatewaySettingsApi.test(typed);
                setNote({ ok: r.ok, text: r.message });
              })
            }
          >
            {busy === 'test' ? 'Testing…' : 'Test connection'}
          </Button>
          {(v.url.source === 'stitch' || v.token.source === 'stitch') && (
            <Button
              variant="ghost"
              disabled={busy !== ''}
              onClick={() =>
                void run('remove', async () => {
                  setView(await gatewaySettingsApi.save({ ...(v.url.source === 'stitch' ? { url: null } : {}), ...(v.token.source === 'stitch' ? { token: null } : {}) }));
                  setNote({ ok: true, text: 'Removed what was saved in Stitch. EasyPanel’s variables count again, if they’re set.' });
                })
              }
            >
              Remove Stitch’s
            </Button>
          )}
        </div>
        {v.updatedAt && (
          <p className="muted small" style={{ marginBottom: 0 }}>
            Last changed {shortWhen(v.updatedAt)}
            {v.updatedByName ? ` by ${v.updatedByName}` : ''}.
          </p>
        )}
      </form>
    </Card>
  );
}
